
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { AuthorizedUser } from '@/types/authorizedUser';
import { Referral } from '@/components/ReferralList';
import { 
  getCurrentUser, 
  isAuthenticated, 
  isAdmin, 
  logoutUser 
} from '@/utils/auth';
import { safeUserRole } from '@/utils/roleUtils';
import { 
  getAllReferrals,
  approveReferral,
  rejectReferral,
} from '@/services/referralService';
import {
  getAuthorizedUsers,
  addAuthorizedUser,
  deleteAuthorizedUser,
  bulkImportUsers,
} from '@/utils/userService';

export const useAdminDashboard = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [user, setUser] = useState<any>(null);
  const [referrals, setReferrals] = useState<Referral[]>([]);
  const [authorizedUsers, setAuthorizedUsers] = useState<AuthorizedUser[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [processingReferrals, setProcessingReferrals] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState("referrals");
  
  useEffect(() => {
    const currentUser = getCurrentUser();
    const userRole = currentUser?.role || 'user';
    
    if (!isAuthenticated() || !isAdmin(safeUserRole(userRole))) {
      navigate('/login');
      return;
    }
    
    setUser(currentUser);
    
    const loadData = async () => {
      setIsLoading(true);
      try {
        const [referralsData, usersData] = await Promise.all([
          getAllReferrals(),
          getAuthorizedUsers(),
        ]);
        
        setReferrals(referralsData);
        setAuthorizedUsers(usersData);
      } catch (error) {
        console.error('Error loading admin dashboard data:', error);
        toast({
          title: "Error",
          description: "Failed to load dashboard data",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };
    
    loadData();
  }, [navigate, toast]);
  
  const handleLogout = async () => {
    try {
      await logoutUser();
      navigate('/login');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };
  
  const handleApproveReferral = async (referralId: string) => {
    setProcessingReferrals(prev => [...prev, referralId]);
    
    try {
      await approveReferral(referralId);
      
      const updatedReferrals = await getAllReferrals();
      setReferrals(updatedReferrals);
      
      toast({
        title: "Referral approved",
        description: "The referral has been approved and points awarded",
      });
      
    } catch (error) {
      console.error('Error approving referral:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to approve referral",
        variant: "destructive",
      });
    } finally {
      setProcessingReferrals(prev => prev.filter(id => id !== referralId));
    }
  };
  
  const handleRejectReferral = async (referralId: string) => {
    setProcessingReferrals(prev => [...prev, referralId]);
    
    try {
      await rejectReferral(referralId);
      
      const updatedReferrals = await getAllReferrals();
      setReferrals(updatedReferrals);
      
      toast({
        title: "Referral rejected",
        description: "The referral has been rejected",
      });
      
    } catch (error) {
      console.error('Error rejecting referral:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to reject referral",
        variant: "destructive",
      });
    } finally {
      setProcessingReferrals(prev => prev.filter(id => id !== referralId));
    }
  };
  
  const handleAddAuthorizedUser = async (userData: Omit<AuthorizedUser, 'id'>) => {
    try {
      await addAuthorizedUser(userData);
      const updatedUsers = await getAuthorizedUsers();
      setAuthorizedUsers(updatedUsers);
    } catch (error) {
      throw error;
    }
  };
  
  const handleDeleteAuthorizedUser = async (userId: string) => {
    try {
      await deleteAuthorizedUser(userId);
      const updatedUsers = await getAuthorizedUsers();
      setAuthorizedUsers(updatedUsers);
    } catch (error) {
      throw error;
    }
  };
  
  const handleBulkImportUsers = async (users: Omit<AuthorizedUser, 'id'>[]) => {
    try {
      await bulkImportUsers(users);
      const updatedUsers = await getAuthorizedUsers();
      setAuthorizedUsers(updatedUsers);
      
      toast({
        title: "Import Complete",
        description: `Successfully imported ${users.length} users`,
      });
    } catch (error) {
      throw error;
    }
  };
  
  const exportReport = (format: 'csv' | 'pdf') => {
    toast({
      title: `Exporting ${format.toUpperCase()}`,
      description: `The report is being downloaded as ${format.toUpperCase()}`,
    });
    
    console.log(`Exporting admin report as ${format}`);
  };

  return {
    user,
    referrals,
    authorizedUsers,
    isLoading,
    processingReferrals,
    activeTab,
    setActiveTab,
    handleLogout,
    handleApproveReferral,
    handleRejectReferral,
    handleAddAuthorizedUser,
    handleDeleteAuthorizedUser,
    handleBulkImportUsers,
    exportReport
  };
};
